import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { portalCommands } from 'src/app/utils/portal-path';
import { UsersService } from 'src/app/services/users.service';
import { AuthService } from 'src/app/services/auth.service';
import { PermissionService } from 'src/app/services/permission.service';
import { User } from '../users.component';
import { TranslateService } from '@ngx-translate/core';
import {
  ACCESS_ACTIONS,
  accessFingerprint,
  actionLabelKey,
  defaultAccessPermissions,
  defaultPermissionForPortal,
  emptyPermission,
  resolveAccessPermissions,
  resolveSidebarPortal,
  serializeAccessPermissions,
  sidebarAccessGroups,
  supportsAction as featureSupportsAction,
  type AccessAction,
  type FeaturePermission,
  type SidebarAccessItem,
  type SidebarPortal,
} from 'src/app/constants/sidebar-access';

interface ClassGroup {
  majorName: string;
  classes: { id: string; name: string }[];
}

interface AccessMatrixGroup {
  label: string;
  items: SidebarAccessItem[];
}

@Component({
  selector: 'app-user-detail',
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.css']
})
export class UserDetailComponent {
  user: User | null = null;
  loading = true;
  errorMessage = '';
  open = false;
  mode = 'edit';
  selectedIdForEdit = '';
  activeTab: 'overview' | 'access' = 'overview';

  readonly tabs: { id: 'overview' | 'access'; labelKey: string; icon: string }[] = [
    { id: 'overview', labelKey: 'USERS.TAB_OVERVIEW', icon: 'person' },
    { id: 'access', labelKey: 'USERS.SIDEBAR_ACCESS', icon: 'lock_open' },
  ];

  readonly accessActions = ACCESS_ACTIONS;
  readonly accessMatrixGroups: AccessMatrixGroup[] = sidebarAccessGroups();

  userPortal: SidebarPortal = 'admin';
  portalLabelKey = 'USERS.PORTAL_ADMIN';
  grantedAccessCount = 0;
  classGroups: ClassGroup[] = [];
  accessDirty = false;
  savingAccess = false;
  accessError = '';
  hasCustomAccess = false;
  showResetConfirm = false;

  private draftById = new Map<string, FeaturePermission>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private usersService: UsersService,
    private authService: AuthService,
    public permissions: PermissionService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    this.route.paramMap.subscribe(async params => {
      const id = params.get('id');
      if (!id) {
        this.router.navigate(portalCommands(this.router, 'settings', 'users'));
        return;
      }
      await this.loadUser(id);
    });
  }

  setTab(tabId: 'overview' | 'access') {
    if (this.activeTab === tabId) {
      return;
    }
    this.activeTab = tabId;
  }

  async loadUser(id: string) {
    this.loading = true;
    this.errorMessage = '';
    try {
      this.user = await this.usersService.getUserById(id);
      if (!this.user) {
        this.errorMessage = 'USERS.NOT_FOUND';
        this.resetAccessState();
      } else {
        this.refreshDerivedState();
      }
    } catch {
      this.errorMessage = 'USERS.LOAD_FAILED';
      this.resetAccessState();
    } finally {
      this.loading = false;
    }
  }

  get usersLink(): string[] {
    return portalCommands(this.router, 'settings', 'users');
  }

  goBack() {
    this.router.navigate(portalCommands(this.router, 'settings', 'users'));
  }

  onEdit() {
    if (!this.user) {
      return;
    }
    this.selectedIdForEdit = this.user.id;
    this.mode = 'edit';
    this.open = true;
  }

  async handleUserUpdated() {
    if (this.user) {
      await this.loadUser(this.user.id);
    }
  }

  get displayName(): string {
    if (!this.user) {
      return '';
    }
    const fullName = [this.user.firstName, this.user.lastName].filter(Boolean).join(' ').trim();
    return fullName || this.user.username;
  }

  get initials(): string {
    if (!this.user) {
      return '?';
    }
    const first = this.user.firstName?.trim()?.[0] || '';
    const last = this.user.lastName?.trim()?.[0] || '';
    if (first || last) {
      return (first + last).toUpperCase();
    }
    return this.user.username.slice(0, 2).toUpperCase();
  }

  get isSystemUser(): boolean {
    return this.user?.isSystemUser === true || this.user?.username === 'config';
  }

  actionLabel(action: AccessAction): string {
    return actionLabelKey(action);
  }

  supportsAction(item: SidebarAccessItem, action: AccessAction): boolean {
    return featureSupportsAction(item, action);
  }

  isGranted(item: SidebarAccessItem, action: AccessAction = 'view'): boolean {
    return this.draftById.get(item.id)?.[action] === true;
  }

  isAccessLocked(item: SidebarAccessItem): boolean {
    return this.isSystemUser && item.id === 'users';
  }

  toggleAccess(item: SidebarAccessItem, action: AccessAction): void {
    if (this.savingAccess || this.isAccessLocked(item) || !this.permissions.canEdit('users') || !featureSupportsAction(item, action)) {
      return;
    }

    const current = this.draftById.get(item.id) || emptyPermission(item.id);
    const next = { ...current, id: item.id };

    if (action === 'view') {
      if (current.view) {
        this.draftById.delete(item.id);
      } else {
        this.draftById.set(item.id, defaultPermissionForPortal(item.id, this.userPortal));
      }
      this.syncAccessView();
      return;
    }

    if (!current.view) {
      this.draftById.set(item.id, {
        id: item.id,
        view: true,
        add: action === 'add',
        edit: action === 'edit',
        delete: action === 'delete',
      });
      this.syncAccessView();
      return;
    }

    next[action] = !current[action];
    this.draftById.set(item.id, next);
    this.syncAccessView();
  }

  async saveAccess(): Promise<void> {
    if (!this.user || this.savingAccess || !this.accessDirty) {
      return;
    }
    this.savingAccess = true;
    this.accessError = '';
    try {
      const sidebarAccess = serializeAccessPermissions([...this.draftById.values()]);
      await this.usersService.editUser(this.user.id, { sidebarAccess });
      await this.afterAccessSaved();
    } catch (error: any) {
      this.accessError = error?.message || 'USERS.ACCESS_SAVE_FAILED';
    } finally {
      this.savingAccess = false;
    }
  }

  askResetAccess(): void {
    if (!this.user || this.savingAccess || (!this.hasCustomAccess && !this.accessDirty)) {
      return;
    }
    this.showResetConfirm = true;
  }

  async confirmResetAccess(): Promise<void> {
    this.showResetConfirm = false;
    await this.resetAccess();
  }

  async resetAccess(): Promise<void> {
    if (!this.user || this.savingAccess) {
      return;
    }
    if (!this.hasCustomAccess) {
      this.draftById = new Map(defaultAccessPermissions(this.userPortal).map(item => [item.id, { ...item }]));
      this.syncAccessView();
      return;
    }
    this.savingAccess = true;
    this.accessError = '';
    try {
      await this.usersService.editUser(this.user.id, { resetSidebarAccess: true });
      await this.afterAccessSaved();
    } catch (error: any) {
      this.accessError = error?.message || 'USERS.ACCESS_SAVE_FAILED';
    } finally {
      this.savingAccess = false;
    }
  }

  private async afterAccessSaved(): Promise<void> {
    if (!this.user) {
      return;
    }
    const id = this.user.id;
    await this.loadUser(id);
    const current = this.authService.getUser();
    if (current && String(current.id) === String(id)) {
      await this.authService.getProfile();
    }
  }

  private refreshDerivedState(): void {
    this.userPortal = resolveSidebarPortal(this.user?.role?.name, {
      isSystemUser: this.isSystemUser,
    });
    this.portalLabelKey = `USERS.PORTAL_${this.userPortal.toUpperCase()}`;
    this.hasCustomAccess = Array.isArray(this.user?.sidebarAccess);
    this.draftById = new Map(
      resolveAccessPermissions(this.userPortal, this.user?.sidebarAccess).map(item => [item.id, { ...item }])
    );
    this.classGroups = this.buildClassGroups();
    this.syncAccessView();
  }

  private syncAccessView(): void {
    const saved = accessFingerprint(resolveAccessPermissions(this.userPortal, this.user?.sidebarAccess));
    const draft = accessFingerprint([...this.draftById.values()]);
    this.accessDirty = saved !== draft;
    this.grantedAccessCount = [...this.draftById.values()].filter(item => item.view).length;
  }

  private resetAccessState(): void {
    this.userPortal = 'admin';
    this.portalLabelKey = 'USERS.PORTAL_ADMIN';
    this.grantedAccessCount = 0;
    this.classGroups = [];
    this.accessDirty = false;
    this.hasCustomAccess = false;
    this.draftById = new Map();
  }

  private buildClassGroups(): ClassGroup[] {
    if (!this.user?.classes?.length) {
      return [];
    }

    const grouped = new Map<string, ClassGroup>();
    for (const cls of this.user.classes as any[]) {
      const majorName = cls.major?.name || this.translate.instant('USERS.UNASSIGNED_MAJOR');
      if (!grouped.has(majorName)) {
        grouped.set(majorName, { majorName, classes: [] });
      }
      grouped.get(majorName)!.classes.push({ id: cls.id, name: cls.name });
    }

    return Array.from(grouped.values()).map(group => ({
      ...group,
      classes: group.classes.sort((a, b) => a.name.localeCompare(b.name, 'ar'))
    }));
  }
}
